/* =============================================
   Hero Slideshow — hero-slideshow.js
   Web component: autoplay, dots, arrows, swipe,
   Shopify theme editor support.
   ============================================= */

if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');

      this.index = 0;
      this.total = this.slides.length;
      this.isFade = this.dataset.transition === 'fade';
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.total > 1;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.timer = null;

      if (this.total <= 1) return;

      this.bindControls();
      this.bindSwipe();
      this.bindAutoplayPausing();
      this.bindEditorEvents();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    /* ---------- Navigation ---------- */

    goTo(i, userInitiated = false) {
      this.index = ((i % this.total) + this.total) % this.total;

      if (this.isFade) {
        this.slides.forEach((slide, idx) => {
          slide.classList.toggle('is-active', idx === this.index);
        });
      } else {
        this.slides.forEach((slide, idx) => {
          slide.classList.toggle('is-active', idx === this.index);
        });
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
      }

      this.dots.forEach((dot, idx) => {
        const active = idx === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (userInitiated) this.restartAutoplay();
    }

    next(userInitiated = false) {
      this.goTo(this.index + 1, userInitiated);
    }

    prev(userInitiated = false) {
      this.goTo(this.index - 1, userInitiated);
    }

    /* ---------- Controls ---------- */

    bindControls() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.dataset.index, 10), true);
        });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev(true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next(true));

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev(true);
        if (e.key === 'ArrowRight') this.next(true);
      });
    }

    /* ---------- Swipe (mobile + mouse drag) ---------- */

    /* ---------- Swipe / drag (mobile touch + desktop mouse) ---------- */

    bindSwipe() {
      this.dragDelta = 0;
      let startX = 0;
      let isDown = false;
      let width = 0;

      const onMove = (e) => {
        if (!isDown) return;
        this.dragDelta = e.clientX - startX;
        // Live drag-follow (slide mode only; fade uses flick behaviour)
        if (!this.isFade) {
          const base = -this.index * width;
          this.track.style.transform = `translateX(${base + this.dragDelta}px)`;
        }
      };

      const onUp = (e) => {
        if (!isDown) return;
        isDown = false;
        this.classList.remove('is-dragging');
        this.dragDelta = e.clientX - startX;

        const threshold = Math.min(80, width * 0.15);
        if (Math.abs(this.dragDelta) > threshold) {
          if (this.dragDelta < 0) this.next(true);
          else this.prev(true);
        } else {
          // Didn't drag far enough — snap back to current slide
          this.goTo(this.index, true);
        }
      };

      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isDown = true;
        startX = e.clientX;
        this.dragDelta = 0;
        width = this.track.clientWidth;
        this.classList.add('is-dragging');
        this.stopAutoplay();
      });

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);

      // Stop the browser's native image/link dragging from hijacking the gesture
      this.track.addEventListener('dragstart', (e) => e.preventDefault());

      // If the pointer moved (swipe), don't let the release count as a
      // click on the full-slide banner link or buttons.
      this.addEventListener(
        'click',
        (e) => {
          if (Math.abs(this.dragDelta) > 10) {
            e.preventDefault();
            e.stopPropagation();
            this.dragDelta = 0;
          }
        },
        true
      );
    }

    /* ---------- Autoplay ---------- */

    startAutoplay() {
      if (!this.autoplayEnabled || this.paused) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.next(), this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      if (!this.autoplayEnabled) return;
      this.stopAutoplay();
      this.startAutoplay();
    }

    bindAutoplayPausing() {
      // Pause on hover (desktop)
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());

      // Pause when tab is hidden
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }

    /* ---------- Shopify theme editor ---------- */

    bindEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest ? e.target.closest('[data-slide]') : null;
        const target = slide || (e.target.querySelector && e.target.querySelector('[data-slide]'));
        if (target && this.contains(target)) {
          this.paused = true;
          this.stopAutoplay();
          this.goTo(parseInt(target.dataset.index, 10));
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target)) {
          this.paused = false;
          this.startAutoplay();
        }
      });
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}